<template>
  <Container>
    <template #header>
      <HeaderFe />
    </template>

    <template #hero>
      <PageHero page="informasi" title="Informasi" current="Informasi"
        subtitle="Berita dan informasi terkini dari jurusan." />
    </template>
    <template v-slot:default>
      <main class="bg-gray-50 min-h-screen pb-10">
        <div class="max-w-[1170px] mx-auto px-4 sm:px-6 xl:px-0 pt-10">

          <!-- Search -->
          <div class="flex justify-end mb-8">
            <div class="relative w-full sm:w-72">
              <i class="fa-solid fa-magnifying-glass absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm pointer-events-none"></i>
              <input
                v-model="search"
                type="text"
                autocomplete="off"
                placeholder="Cari berita..."
                class="w-full pl-9 pr-4 py-2 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent shadow-sm"
              />
            </div>
          </div>

          <!-- Grid -->
          <div v-if="paginatedNews.length" class="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            <article
              v-for="item in paginatedNews"
              :key="item.id"
              class="group flex flex-col bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden hover:shadow-md transition-shadow duration-300"
            >
              <!-- Thumbnail -->
              <Link
                :href="`/read/${item.slug}`"
                class="relative block aspect-[16/9] overflow-hidden bg-gray-100 flex-shrink-0"
              >
                <img
                  v-if="getImage(item)"
                  :src="getImage(item)"
                  :alt="item.title"
                  class="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                  @error="$event.target.style.display='none'"
                />
                <div v-else class="w-full h-full flex items-center justify-center text-gray-300">
                  <i class="fa-regular fa-image text-3xl"></i>
                </div>

                <!-- Multi-image badge -->
                <span
                  v-if="Array.isArray(item.image) && item.image.length > 1"
                  class="absolute top-2 right-2 flex items-center gap-1 px-2 py-0.5 text-xs font-medium text-white bg-black/60 rounded-full backdrop-blur-sm"
                >
                  <i class="fa-solid fa-images text-[10px]"></i>
                  {{ item.image.length }}
                </span>
              </Link>

              <!-- Body -->
              <div class="flex flex-col flex-1 p-4">
                <!-- Title -->
                <h2 class="font-semibold text-gray-800 leading-snug mb-2 line-clamp-2 group-hover:text-blue-700 transition-colors duration-200">
                  <Link :href="`/read/${item.slug}`" class="hover:underline underline-offset-2">
                    {{ item.title }}
                  </Link>
                </h2>

                <!-- Excerpt -->
                <p class="text-sm text-gray-500 leading-relaxed line-clamp-2 flex-1 mb-3">
                  {{ stripHtml(item.content) }}
                </p>

                <!-- Tags -->
                <div v-if="item.tags && item.tags.length" class="flex flex-wrap gap-1.5 mb-3">
                  <span
                    v-for="tag in item.tags.slice(0, 3)"
                    :key="tag"
                    class="px-2 py-0.5 text-xs font-medium text-blue-700 bg-blue-50 border border-blue-100 rounded-full"
                  >
                    #{{ tag }}
                  </span>
                </div>

                <!-- Meta -->
                <div class="flex items-center justify-between text-xs text-gray-400 pt-3 border-t border-gray-100 mt-auto">
                  <span class="flex items-center gap-1.5 min-w-0">
                    <i class="fa-regular fa-calendar flex-shrink-0"></i>
                    <span class="truncate">{{ formatTanggal(item.publish_date) }}</span>
                  </span>
                  <div class="flex items-center gap-3 flex-shrink-0">
                    <span v-if="item.author" class="flex items-center gap-1">
                      <i class="fa-regular fa-user"></i>
                      <span class="max-w-[80px] truncate">{{ item.author }}</span>
                    </span>
                    <span class="flex items-center gap-1">
                      <i class="fa-regular fa-eye"></i>
                      {{ item.formatted_view_count || '0' }}
                    </span>
                  </div>
                </div>

                <!-- Share -->
                <div class="flex items-center gap-2 pt-3 mt-2 border-t border-gray-100">
                  <span class="text-xs text-gray-400">Bagikan:</span>
                  <a
                    :href="getFacebookShareUrl(item)"
                    target="_blank"
                    rel="noopener noreferrer nofollow"
                    class="w-7 h-7 flex items-center justify-center rounded-full bg-blue-600 text-white hover:bg-blue-700 transition-colors text-xs"
                    aria-label="Bagikan ke Facebook"
                    @click.stop
                  >
                    <i class="fa-brands fa-facebook-f"></i>
                  </a>
                  <a
                    :href="getWhatsappShareUrl(item)"
                    target="_blank"
                    rel="noopener noreferrer nofollow"
                    class="w-7 h-7 flex items-center justify-center rounded-full bg-green-500 text-white hover:bg-green-600 transition-colors text-xs"
                    aria-label="Bagikan ke WhatsApp"
                    @click.stop
                  >
                    <i class="fa-brands fa-whatsapp"></i>
                  </a>
                </div>
              </div>
            </article>
          </div>

          <!-- Empty state -->
          <div v-else class="flex flex-col items-center justify-center py-20 text-center">
            <i class="fa-regular fa-newspaper text-5xl text-gray-300 mb-4"></i>
            <h3 class="text-lg font-semibold text-gray-700 mb-1">Berita tidak ditemukan</h3>
            <p class="text-sm text-gray-400">
              {{ search
                ? `Tidak ada berita yang cocok dengan "${search}".`
                : 'Belum ada berita yang tersedia.' }}
            </p>
          </div>

          <!-- Pagination -->
          <div v-if="totalPages > 1" class="flex justify-center items-center gap-1.5 mt-10">
            <button
              @click="goToPage(page - 1)"
              :disabled="page === 1"
              class="w-9 h-9 flex items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-500 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              <i class="fa-solid fa-chevron-left text-xs"></i>
            </button>

            <button
              v-for="p in totalPages"
              :key="p"
              @click="goToPage(p)"
              :class="[
                'w-9 h-9 flex items-center justify-center rounded-lg text-sm font-medium transition-colors border',
                p === page
                  ? 'bg-blue-700 border-blue-700 text-white'
                  : 'bg-white border-gray-200 text-gray-600 hover:bg-blue-50 hover:border-blue-200 hover:text-blue-700'
              ]"
            >
              {{ p }}
            </button>

            <button
              @click="goToPage(page + 1)"
              :disabled="page === totalPages"
              class="w-9 h-9 flex items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-500 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              <i class="fa-solid fa-chevron-right text-xs"></i>
            </button>
          </div>

        </div>
      </main>

      <!-- Image lightbox modal -->
      <Teleport to="body">
        <div
          v-if="lightbox.show"
          class="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm"
          @click.self="closeLightbox"
        >
          <div class="relative w-full max-w-3xl mx-4">
            <!-- Close -->
            <button
              @click="closeLightbox"
              class="absolute -top-10 right-0 text-white/70 hover:text-white text-2xl transition-colors"
              aria-label="Tutup"
            >
              <i class="fa-solid fa-times"></i>
            </button>

            <!-- Image -->
            <img
              :src="lightbox.images[lightbox.index]"
              :alt="`Gambar ${lightbox.index + 1}`"
              class="w-full max-h-[80vh] object-contain rounded-lg"
            />

            <!-- Nav -->
            <div v-if="lightbox.images.length > 1" class="flex items-center justify-between mt-4">
              <button
                @click="lightbox.index = Math.max(0, lightbox.index - 1)"
                :disabled="lightbox.index === 0"
                class="px-4 py-2 text-sm text-white bg-white/20 rounded-lg hover:bg-white/30 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              >
                <i class="fa-solid fa-chevron-left mr-1"></i> Sebelumnya
              </button>
              <span class="text-sm text-white/70">{{ lightbox.index + 1 }} / {{ lightbox.images.length }}</span>
              <button
                @click="lightbox.index = Math.min(lightbox.images.length - 1, lightbox.index + 1)"
                :disabled="lightbox.index === lightbox.images.length - 1"
                class="px-4 py-2 text-sm text-white bg-white/20 rounded-lg hover:bg-white/30 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              >
                Selanjutnya <i class="fa-solid fa-chevron-right ml-1"></i>
              </button>
            </div>

            <!-- Thumbnails -->
            <div v-if="lightbox.images.length > 1" class="flex gap-2 mt-3 overflow-x-auto pb-1">
              <button
                v-for="(img, i) in lightbox.images"
                :key="i"
                @click="lightbox.index = i"
                :class="[
                  'flex-shrink-0 w-14 h-14 rounded-md overflow-hidden border-2 transition-all',
                  lightbox.index === i ? 'border-blue-400' : 'border-white/20 hover:border-white/50'
                ]"
              >
                <img :src="img" :alt="`Thumb ${i + 1}`" class="w-full h-full object-cover" />
              </button>
            </div>
          </div>
        </div>
      </Teleport>
    </template>

    <template #footer>
      <FooterFe />
    </template>
  </Container>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted } from "vue";
import { Link } from "@inertiajs/vue3";
import Container from "@/Layouts/Container.vue";
import HeaderFe from "@/Components/HeaderFe.vue";
import FooterFe from "@/Components/FooterFe.vue";
import PageHero from "@/Components/Web/PageHero.vue";
import dayjs from "dayjs";
import "dayjs/locale/id";
dayjs.locale("id");

const props = defineProps({
  newsall: {
    type: [Object, Array],
    required: true,
    default: () => [],
  },
});

const search = ref("");
const page = ref(1);
const PER_PAGE = 9;

// Lightbox state
const lightbox = ref({ show: false, images: [], index: 0 });

const formatTanggal = (tanggal) =>
  tanggal ? dayjs(tanggal).format("D MMMM YYYY") : "-";

const stripHtml = (html) =>
  (html || "")
    .replace(/<[^>]+>/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, 120) || "–";

const getImage = (item) => {
  if (Array.isArray(item.image) && item.image.length) return item.image[0];
  if (typeof item.image === "string" && item.image) return item.image;
  return null;
};

const getShareUrl = (item) =>
  `${window.location.origin}/share/${item.slug}`;

const getFacebookShareUrl = (item) =>
  `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(getShareUrl(item))}`;

const getWhatsappShareUrl = (item) =>
  `https://wa.me/?text=${encodeURIComponent(`${item.title} ${getShareUrl(item)}`)}`;


// Normalize data from Inertia resource collection or plain array
const allNews = computed(() => {
  if (Array.isArray(props.newsall)) return props.newsall;
  if (Array.isArray(props.newsall?.data)) return props.newsall.data;
  return [];
});

const filteredNews = computed(() => {
  const q = search.value.toLowerCase().trim();
  if (!q) return allNews.value;
  return allNews.value.filter((item) =>
    [item.title, item.author, ...(item.tags || [])]
      .filter(Boolean)
      .some((v) => String(v).toLowerCase().includes(q))
  );
});

const totalPages = computed(() => Math.ceil(filteredNews.value.length / PER_PAGE));

const paginatedNews = computed(() => {
  const start = (page.value - 1) * PER_PAGE;
  return filteredNews.value.slice(start, start + PER_PAGE);
});

const goToPage = (p) => {
  page.value = Math.max(1, Math.min(p, totalPages.value));
  window.scrollTo({ top: 0, behavior: "smooth" });
};

watch(search, () => { page.value = 1; });

// Lightbox
const openLightbox = (item) => {
  const images = Array.isArray(item.image) ? item.image : item.image ? [item.image] : [];
  if (!images.length) return;
  lightbox.value = { show: true, images, index: 0 };
};
const closeLightbox = () => { lightbox.value.show = false; };

const handleKey = (e) => {
  if (!lightbox.value.show) return;
  if (e.key === "Escape") closeLightbox();
  if (e.key === "ArrowRight") lightbox.value.index = Math.min(lightbox.value.images.length - 1, lightbox.value.index + 1);
  if (e.key === "ArrowLeft")  lightbox.value.index = Math.max(0, lightbox.value.index - 1);
};

onMounted(() => document.addEventListener("keydown", handleKey));
onUnmounted(() => document.removeEventListener("keydown", handleKey));
</script>

<style scoped>
.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
</style>
